'use client';

import { Box, Typography } from '@coolblue-development/freddy';
import viewElementTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/viewElementTrigger';
import { TrackWhenVisible } from '@coolblue-development/next-analytics/client';
import PropertiesSummaryBlock from '@features/product/components/Specifications/PropertiesSummaryBlock';
import PropertiesTable from '@features/product/components/Specifications/PropertiesTable';
import type { Property, PropertyGroup } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import React, { useState } from 'react';

import FullSpecificationsLink from './FullSpecificationLink';
import SpecificationsModal from './SpecificationsModal';

type Props = {
  productId: number;
  productTypeId: number;
  properties: {
    summary: Property[];
    propertyGroups: PropertyGroup[];
  };
  showAdviceCuesBlock?: boolean;
};

function ShortSpecifications({
  productId,
  productTypeId,
  properties: { summary, propertyGroups },
  showAdviceCuesBlock = false
}: Props) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('webshop');
  const { isMobile } = useBreakpoints();
  const maxProperties = isMobile ? 5 : showAdviceCuesBlock ? 6 : 8;
  const filteredProperties = summary.slice(0, maxProperties);

  return (
    <TrackWhenVisible
      trigger={viewElementTrigger({
        sub_feature: 'short_specifications'
      })}
    >
      <Box>
        <Typography variant='h3' mb={200}>
          {t('productPage.shortSpecifications')}
        </Typography>

        {showAdviceCuesBlock ? (
          <PropertiesSummaryBlock properties={filteredProperties} productTypeId={productTypeId} />
        ) : (
          <PropertiesTable properties={filteredProperties} productTypeId={productTypeId} isShort />
        )}

        <FullSpecificationsLink onMobileClick={() => setOpen(true)}>
          {t('productPage.moreSpecifications')}
        </FullSpecificationsLink>

        {isMobile && (
          <SpecificationsModal
            productId={productId}
            open={open}
            onClose={() => setOpen(false)}
            propertyGroups={propertyGroups}
            productTypeId={productTypeId}
          />
        )}
      </Box>
    </TrackWhenVisible>
  );
}

export default ShortSpecifications;
